import { useQuery } from '@tanstack/react-query'
import { ExternalLink, MapPinOff } from 'lucide-react'
import { queryKeys } from '@/lib/query-client'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { EmptyState } from '@/components/ui/States'
import { formatDistance, formatDuration, hasSomethingToShow, type RouteTarget } from './geo'
import { fetchDrivenRoute } from './routing'
import { RouteMap } from './RouteMap'
import styles from './RouteMapDialog.module.css'

interface RouteMapDialogProps {
  /** The journey to draw, or null when the dialog is closed. */
  target: RouteTarget | null
  onClose: () => void
}

/**
 * One booking's journey on an OpenStreetMap canvas.
 *
 * Shared by the Trips page and the approval queue, which hold different records
 * but the same six coordinate fields — see {@link toRouteTarget}. Both screens
 * had the addresses as text only, which answers "where is this going?" for
 * somewhere the operator already knows and nothing else.
 */
export function RouteMapDialog({ target, onClose }: RouteMapDialogProps) {
  const pickup = target?.pickup.point ?? null
  const dropoff = target?.dropoff.point ?? null

  // Both ends or nothing: a route needs somewhere to start and somewhere to
  // finish, and half of one is not a shorter route, it is a different question.
  const journey = pickup && dropoff ? { from: pickup, to: dropoff } : null

  const routeQuery = useQuery({
    queryKey: journey
      ? queryKeys.routes.driving(journey.from.lat, journey.from.lng, journey.to.lat, journey.to.lng)
      : queryKeys.routes.all,
    queryFn: ({ signal }) =>
      journey ? fetchDrivenRoute(journey.from, journey.to, signal) : Promise.resolve(null),
    enabled: journey !== null,
    // Roads do not move. Re-asking a public router every time an operator
    // reopens the same booking is rude to it and slower for them.
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    // The fetch already falls back to a straight line rather than failing, so a
    // retry would only delay the drawing.
    retry: false,
  })

  const route = routeQuery.data ?? null
  const mappable = target !== null && hasSomethingToShow(target)

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      size="xl"
      title={target ? `Route · ${target.reference}` : 'Route'}
      description="Pickup and drop-off on OpenStreetMap, with the driving route between them."
      footer={
        <>
          {pickup && dropoff ? (
            <a
              className={styles.externalLink}
              href={`https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${pickup.lat}%2C${pickup.lng}%3B${dropoff.lat}%2C${dropoff.lng}`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink aria-hidden="true" />
              Open on openstreetmap.org
            </a>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {target ? (
        mappable ? (
          <div className={styles.body}>
            <dl className={styles.stops}>
              <div className={styles.stop}>
                <dt className={styles.stopLabel}>
                  <span className={`${styles.swatch} ${styles.swatchPickup}`} aria-hidden="true" />
                  Pickup
                </dt>
                <dd className={styles.stopValue}>
                  {target.pickup.address ?? 'Address not recorded'}
                  {target.pickup.point ? null : (
                    <span className={styles.stopWarning}> · not on the map</span>
                  )}
                </dd>
              </div>
              <div className={styles.stop}>
                <dt className={styles.stopLabel}>
                  <span className={`${styles.swatch} ${styles.swatchDropoff}`} aria-hidden="true" />
                  Drop-off
                </dt>
                <dd className={styles.stopValue}>
                  {target.dropoff.address ?? 'Not set'}
                  {target.dropoff.point ? null : (
                    <span className={styles.stopWarning}> · not on the map</span>
                  )}
                </dd>
              </div>
            </dl>

            <RouteMap pickup={target.pickup} dropoff={target.dropoff} route={route} />

            <p className={styles.summary} role="status">
              {describeRoute(routeQuery.isLoading, route, journey !== null)}
            </p>
          </div>
        ) : (
          <EmptyState
            icon={<MapPinOff aria-hidden="true" />}
            title="This booking has no coordinates"
            description={
              // Bookings taken before the app started sending a position, and
              // any taken over the phone, carry an address and nothing else.
              // There is no point opening a map on a text string.
              'Only the written addresses were recorded, so there is nothing to place on a map. The pickup and drop-off are shown in full on the booking itself.'
            }
          />
        )
      ) : null}
    </Dialog>
  )
}

function describeRoute(
  loading: boolean,
  route: { distanceMetres: number; durationSeconds: number | null; kind: string } | null,
  routable: boolean,
): string {
  if (!routable) {
    return 'Only one end of this journey has a position, so there is no route to draw — the map shows the point that was recorded.'
  }
  if (loading || !route) return 'Working out the driving route…'

  const distance = formatDistance(route.distanceMetres)

  if (route.kind === 'straight') {
    return `Routing was unavailable, so the dashed line is the direct distance — ${distance} in a straight line, not by road.`
  }

  const duration = route.durationSeconds === null ? null : formatDuration(route.durationSeconds)
  return duration
    ? `${distance} by road · about ${duration} driving in free-flowing traffic.`
    : `${distance} by road.`
}
